import React, { useState } from 'react';
import axios from 'axios';
import MultiDeviceSelector from './MultiDeviceSelector';

const ModalCreateVideowall = ({ isVisible, onClose }) => {
    const [nom, setNom] = useState('');
    const [serverPin, setServerPin] = useState('');
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // 'server' ou 'clients' pour savoir quel champ on remplit
    const [selectorMode, setSelectorMode] = useState(null); 

    const token = localStorage.getItem("token");
    const headers = {
        'x-access-token': token,
        'Content-Type': 'application/json'
    };

    const handleClose = (e) => {
        if (e && e.target.id === "wrapper") onClose();
    };

    // Logique de réception des données du sélecteur
    const handleAddDevices = (selectedPins) => {
        if (selectorMode === 'server') {
            // On prend le premier élément sélectionné pour le serveur
            setServerPin(selectedPins[0] || '');
            // Si le nouveau serveur était déjà dans les clients, on le retire des clients
            if (selectedPins[0]) {
                setClients(prev => prev.filter(c => c !== selectedPins[0]));
            }
        } else if (selectorMode === 'clients') {
            // Fusionner les nouveaux clients en évitant les doublons
            setClients(prev => Array.from(new Set([...prev, ...selectedPins])));
        }
        setSelectorMode(null);
    };

    const handleRemoveClient = (pin) => {
        setClients(clients.filter(c => c !== pin));
    };

    const handleCreate = () => {
        if (!nom || !serverPin || clients.length === 0) {
            alert("Veuillez remplir le nom, choisir un serveur et au moins un client.");
            return;
        }

        setIsLoading(true);
        const dataVw = {
            nom: nom,
            pin_code_serveur: serverPin,
            pin_code_client: clients,
            notes: ""
        };

        axios.post(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/add_clients_videowall`, 
            { dataVw }, 
            { headers }
        )
        .then(() => {
            alert("Video Wall créé avec succès !");
            setNom(''); setServerPin(''); setClients([]); // Reset
            onClose();
        })
        .catch(err => {
            console.error(err);
            alert("Erreur lors de la création.");
        })
        .finally(() => setIsLoading(false));
    };

    if (!isVisible) return null;

    return (
        <>
            {/* Modal Principal (z-50) */}
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" id="wrapper" onClick={handleClose}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                        <h2 className="text-lg font-semibold text-slate-800">Nouveau Video Wall</h2>
                        <button className="text-slate-400 hover:text-slate-600 text-xl leading-none" onClick={onClose}>&times;</button>
                    </div>

                    <div className="flex flex-col gap-4 px-6 py-5">
                    {/* Champ Nom */}
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nom du groupe</label>
                        <input
                            type="text"
                            value={nom}
                            onChange={(e) => setNom(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                            placeholder="Ex: Mur d'écrans Accueil"
                        />
                    </div>

                    {/* Section SERVEUR (Dissociée) */}
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3">
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-indigo-600">Serveur Maître </label>
                        <div className="flex gap-2">
                            <div className="flex-1 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-slate-800">
                                {serverPin || <span className="italic text-slate-400">Aucun serveur sélectionné</span>}
                            </div>
                            <button
                                onClick={() => setSelectorMode('server')}
                                className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600"
                            >
                                {serverPin ? 'Modifier' : 'Choisir'}
                            </button>
                        </div>
                    </div>

                    {/* Section CLIENTS (Dissociée) */}
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                        <div className="mb-2 flex items-center justify-between">
                            <label className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Écrans Clients </label>
                            <button
                                onClick={() => setSelectorMode('clients')}
                                disabled={!serverPin} // Désactivé tant qu'on n'a pas de serveur
                                className={`rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors ${!serverPin ? 'bg-slate-300' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                            >
                                + Ajouter Clients
                            </button>
                        </div>
                        <div className="max-h-32 overflow-auto rounded-lg border border-slate-200 bg-white p-2">
                            {clients.length > 0 ? (
                                <ul className="divide-y divide-slate-100">
                                    {clients.map(pin => (
                                        <li key={pin} className="flex items-center justify-between py-1 text-sm">
                                            <span className="font-mono text-slate-700">{pin}</span>
                                            <button onClick={() => handleRemoveClient(pin)} className="font-medium text-red-500 hover:text-red-700">Retirer</button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="py-2 text-center text-xs italic text-slate-400">
                                    {!serverPin ? "Choisissez d'abord un serveur" : "Aucun client ajouté"}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 flex justify-between border-t border-slate-200 pt-4">
                        <button onClick={onClose} className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50">
                            Annuler
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={isLoading || !serverPin || clients.length === 0}
                            className="rounded-lg bg-indigo-500 px-6 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-indigo-600 disabled:bg-slate-300 disabled:shadow-none"
                        >
                            {isLoading ? 'Création...' : 'Valider le Video Wall'}
                        </button>
                    </div>
                    </div>
                </div>
            </div>

            {/* Le Sélecteur avec un z-index plus haut (z-[60]) */}
            {selectorMode && (
                <div className="relative z-[60]">
                    <MultiDeviceSelector
                        isVisible={true}
                        onClose={() => setSelectorMode(null)}
                        onSubmit={handleAddDevices}
                        // Si mode client : on exclut le serveur des choix possibles
                        currentPinCode={selectorMode === 'clients' ? serverPin : ""} 
                    />
                </div>
            )}
        </>
    );
};

export default ModalCreateVideowall;